// Configuration for email whitelist
// This can be easily modified to add or remove approved email addresses

export const EMAIL_WHITELIST_CONFIG = {
  // Global list of approved email addresses that can access the entire dashboard
  approvedEmails: [
    // Add more approved email addresses here as needed
  ],
  
  // Whether to enable case-sensitive email matching (default: false)
  caseSensitive: false,
  
  // Whether to trim whitespace from emails before comparison (default: true)
  trimWhitespace: true,
  
  // Default redirect path for unauthorized users
  unauthorizedRedirectPath: '/auth/unauthorized',
  
  // Default redirect path for login
  loginRedirectPath: '/auth/login',
  
  // Path-based whitelist mapping
  // Format: { '/path': ['email1@domain.com', 'email2@domain.com'] }
  // Supports wildcards: '/venues/*' will match /venues/123, /venues/456, etc.
  // Example patterns:
  // '/admin/reports': ['report-user@example.com'],     // Exact path
  // '/camp-series/*': ['camp-manager@example.com'],    // All camp series paths
  // '/admin/*': ['admin-user@example.com'],            // All admin paths
  // '/*/settings': ['settings-editor@example.com']     // All settings paths
  pathWhitelists: {
    // '/camp-series/*': ['camp-manager@example.com'],
    // '/reports/*': ['report-user@example.com'],
  }
};

// Development/Testing emails (can be easily toggled)
export const DEV_EMAIL_WHITELIST = [
  'test@example.com',
  'developer@localhost.com',
  'admin@test.com'
];

/**
 * Match a path against a pattern that may include wildcards (*)
 * @param path - The actual path being accessed (e.g., '/camp-series/123' or '/admin/reports/detail')
 * @param pattern - The pattern to match against (e.g., '/camp-series/*' or '/admin/*')
 * @returns boolean - True if path matches pattern, false otherwise
 */
export function matchPathPattern(path: string, pattern: string): boolean {
  // Exact match
  if (path === pattern) {
    return true;
  }
  
  // Convert wildcard pattern to regex
  // Replace * with a regex that matches any characters (including multiple path segments)
  // This allows /camp-series/* to match /camp-series/123/edit
  const regexPattern = pattern.replace(/\*/g, '.*');
  const regex = new RegExp(`^${regexPattern}$`);
  
  return regex.test(path);
}

/**
 * Get the whitelist for a specific path
 * Supports both global emails and path-specific whitelists
 * @param path - The route path to get the whitelist for (e.g., '/camp-series/123')
 * @param isDevelopment - Whether to include development emails
 * @returns string[] - Array of whitelisted email addresses for this path
 */
export function getPathWhitelist(path: string, isDevelopment: boolean = false): string[] {
  const matchedWhitelists = new Set<string>();
  
  // Always include global whitelist
  EMAIL_WHITELIST_CONFIG.approvedEmails.forEach(email => {
    matchedWhitelists.add(email);
  });
  
  // Check all path patterns in the config
  for (const pattern in EMAIL_WHITELIST_CONFIG.pathWhitelists) {
    if (matchPathPattern(path, pattern)) {
      EMAIL_WHITELIST_CONFIG.pathWhitelists[pattern].forEach(email => {
        matchedWhitelists.add(email);
      });
    }
  }
  
  const result = Array.from(matchedWhitelists);
  
  if (isDevelopment) {
    return [...new Set([...result, ...DEV_EMAIL_WHITELIST])]; // Remove duplicates using Set
  }
  
  return result;
}

// Function to merge production and development emails based on environment
export function getEmailWhitelist(isDevelopment: boolean = false): string[] {
  const baseEmails = [...EMAIL_WHITELIST_CONFIG.approvedEmails];
  
  if (isDevelopment) {
    return [...baseEmails, ...DEV_EMAIL_WHITELIST];
  }
  
  return baseEmails;
}
